import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { updateTuiTargetElementData } from '@office-start/teaching-ui-service';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { ObservableScope } from '../shared/models/observable-scope.enum';
import { EventService } from '../shared/services/event.service';
import { FeedbackService } from '../shared/services/feedback.service';
import { WindowService } from '../shared/services/window.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';

import './feedback.component.css';

@Component({
    selector: 'ohp-feedback',
    templateUrl: './feedback.component.html'
})
export class FeedbackComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('feedbackContainer') public feedbackContainer: ElementRef;

    // Strings needed to render feedback
    public header: string;
    public minimizedHeaderAriaLabel: string;

    private supportOffset = 0;
    private flexPaneOffset = 0;
    private pageOffset = 0;
    private paneOpenSubscription: Subscription;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly feedbackService: FeedbackService,
        private readonly locService: LocService,
        private readonly contextService: ContextService,
        private readonly eventService: EventService,
        private readonly renderer: Renderer2,
        private readonly windowService: WindowService
    ) {
    }

    public ngOnInit() {
        this.header = this.locService.FeedbackHeader;
        this.minimizedHeaderAriaLabel = this.locService.FeedbackMinimizedAriaLabel;
    }

    public ngAfterViewInit() {
        this.paneOpenSubscription = this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
            filter(event => event.name === AngularEvents.flexPaneOpen && !!event.args && !!event.args.length))
            .subscribe(event => {
                // TODO: Consider figuring out how to move support central depending on scorecard
                if (this.capabilitiesService.capabilities.supportCentralEnabled && this.supportOffset) {
                    return;
                }
                let isOpen: boolean, flexPaneWidth: number;
                [isOpen, flexPaneWidth] = event.args;
                this.flexPaneOffset = isOpen ? flexPaneWidth : 0;
                this.positionFeedbackElement();
            });

        this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.supportCentralLoaded && !!event.args && !!event.args.length),
            take(1))
            .toPromise()
            .then(event => {
                this.supportOffset = event.args[0];
                this.positionFeedbackElement();
            });

        this.updatePageOffset();
    }

    public ngOnDestroy() {
        if (this.paneOpenSubscription) {
            this.paneOpenSubscription.unsubscribe();
        }

        updateTuiTargetElementData('Feedback_Button', null);
    }

    public openFeedback(): void {
        this.feedbackService.loadOCVFeedback(() => this.feedbackContainer.nativeElement.focus());
    }

    public positionFeedbackElement() {
        const positionAttr = this.contextService.isRtl ? 'left' : 'right';
        const offset = this.flexPaneOffset + (this.supportOffset || this.pageOffset);
        this.renderer.setStyle(this.feedbackContainer.nativeElement, positionAttr, `${offset}px`);
        // Register feedback tui element
        updateTuiTargetElementData('Feedback_Button', { element: this.feedbackContainer.nativeElement });
    }

    public updatePageOffset() {
        const pageOffset = this.windowService.nativeWindow.innerWidth < 480 ? 24 : 50;

        if (pageOffset !== this.pageOffset) {
            this.pageOffset = pageOffset;
            this.positionFeedbackElement();
        }
    }
}
